import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import { Badge } from "./badge";
import { getActionLabel, ENTITY_LABELS, BITACORA_ACTIONS } from "../utils/bitacora";
import {
  ClipboardDocumentListIcon,
  FunnelIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";
const PAGE_SIZE = 50;

const ENTITY_BADGE_COLORS = {
  event: "emerald",
  payment: "blue",
  refund: "orange",
  lead: "purple",
  lead_nota: "purple",
  event_nota: "emerald",
  venue: "zinc",
  concepto: "yellow",
  lead_columns: "pink",
  cotizacion: "cyan",
};

function formatTimestamp(ts) {
  if (!ts) return "-";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Mexico_City",
  });
}

function formatDetails(details) {
  if (!details || typeof details !== "object") return null;
  const parts = [];

  // Status change: from → to
  if (details.from && details.to) {
    parts.push(
      <span key="status" className="inline-flex items-center gap-1.5">
        <span className="text-zinc-500">{details.from}</span>
        <span className="text-zinc-400">&rarr;</span>
        <span className="font-medium text-zinc-700 dark:text-zinc-200">{details.to}</span>
      </span>
    );
  }

  // Amount changes
  if (details.oldMonto != null && details.newMonto != null) {
    parts.push(
      <span key="montoChange" className="inline-flex items-center gap-1.5">
        <span className="text-zinc-500">${Number(details.oldMonto).toLocaleString("es-MX")}</span>
        <span className="text-zinc-400">&rarr;</span>
        <span className="font-medium text-zinc-700 dark:text-zinc-200">${Number(details.newMonto).toLocaleString("es-MX")}</span>
      </span>
    );
  } else if (details.monto != null) {
    parts.push(
      <span key="monto" className="font-medium text-zinc-700 dark:text-zinc-200">
        ${Number(details.monto).toLocaleString("es-MX")}
      </span>
    );
  }

  if (details.precio != null && !details.oldMonto) {
    parts.push(
      <span key="precio" className="font-medium text-zinc-700 dark:text-zinc-200">
        ${Number(details.precio).toLocaleString("es-MX")}
      </span>
    );
  }

  if (details.metodo) {
    parts.push(
      <span key="metodo" className="text-zinc-500">{details.metodo}</span>
    );
  }

  if (details.reason) {
    parts.push(
      <span key="reason" className="text-zinc-500 italic">{details.reason}</span>
    );
  }

  if (details.eventType) {
    const typeLabels = { event: "Evento", scouting: "Scouting", reunion: "Reunión", bloqueo: "Bloqueo" };
    parts.push(
      <span key="eventType" className="text-zinc-500">{typeLabels[details.eventType] || details.eventType}</span>
    );
  }

  if (details.precioUnitario != null) {
    parts.push(
      <span key="pu" className="text-zinc-500">${Number(details.precioUnitario).toLocaleString("es-MX")}/u</span>
    );
  }

  if (details.ivaEnabled != null) {
    parts.push(
      <span key="iva" className={`font-medium ${details.ivaEnabled ? "text-emerald-600" : "text-zinc-500"}`}>
        IVA {details.ivaEnabled ? "activado" : "desactivado"}
      </span>
    );
  }

  // Cotización: folio on top, then desglose + total below
  if (details.folio || (Array.isArray(details.desglose) && details.desglose.length > 0) || details.total != null) {
    const fmtMXN = (v) => {
      const n = Number(v);
      if (n < 0) return `($${Math.abs(n).toLocaleString("es-MX", { minimumFractionDigits: 2 })})`;
      return `$${n.toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
    };
    parts.push(
      <span key="cotizacion-block" className="flex flex-col gap-0.5">
        {details.folio && (
          <span className="font-semibold text-zinc-700 dark:text-zinc-200 text-xs">{details.folio}</span>
        )}
        {Array.isArray(details.desglose) && details.desglose.length > 0 && details.desglose.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 whitespace-nowrap pl-2">
            <span className="text-zinc-600 dark:text-zinc-300">{item.concepto}</span>
            <span className="text-zinc-400">×{item.unidades}</span>
            <span className="text-zinc-500">{fmtMXN(item.subtotal)}</span>
          </span>
        ))}
        {details.total != null && (
          <span className="font-semibold text-zinc-800 dark:text-zinc-100 pl-2 border-t border-zinc-200 dark:border-zinc-700 pt-0.5 mt-0.5">
            Total: ${Number(details.total).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
          </span>
        )}
      </span>
    );
  }

  // Proveedor-related details
  if (details.proveedorNombre) {
    parts.push(
      <span key="proveedor" className="text-zinc-500">
        Proveedor:{" "}
        <span className="font-medium text-zinc-700 dark:text-zinc-200">
          {details.proveedorNombre}
        </span>
      </span>
    );
  }
  if (details.eventNombre) {
    parts.push(
      <span key="evento" className="text-zinc-500">
        Evento:{" "}
        <span className="font-medium text-zinc-700 dark:text-zinc-200">
          {details.eventNombre}
        </span>
      </span>
    );
  }
  if (details.categoria) {
    parts.push(
      <span key="categoria" className="text-zinc-500">
        Categoría:{" "}
        <span className="font-medium text-zinc-700 dark:text-zinc-200">
          {details.categoria}
        </span>
      </span>
    );
  }
  if (details.tipo === "evento" || details.tipo === "general") {
    parts.push(
      <span key="tipo" className="text-zinc-500 italic">
        {details.tipo === "evento" ? "del evento" : "general"}
      </span>
    );
  }
  if (details.contactoNombre) {
    parts.push(
      <span key="contacto" className="text-zinc-500">
        Contacto:{" "}
        <span className="font-medium text-zinc-700 dark:text-zinc-200">
          {details.contactoNombre}
        </span>
      </span>
    );
  }

  // Field-level changes (e.g. event edit diff) — render as compact list
  if (Array.isArray(details.changes) && details.changes.length > 0) {
    const changesList = (
      <span key="changes" className="inline-flex flex-col gap-0.5">
        {details.changes.map((ch, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span className="font-medium text-zinc-600 dark:text-zinc-300">{ch.field}:</span>
            <span className="text-zinc-500 truncate max-w-[120px]" title={ch.from}>{ch.from}</span>
            <span className="text-zinc-400">&rarr;</span>
            <span className="font-medium text-zinc-700 dark:text-zinc-200 truncate max-w-[120px]" title={ch.to}>{ch.to}</span>
          </span>
        ))}
      </span>
    );
    parts.push(changesList);
  }

  if (parts.length === 0) return null;

  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-zinc-300 dark:text-zinc-600">·</span>}
          {part}
        </React.Fragment>
      ))}
    </span>
  );
}

const ACTION_GROUP_LABELS = {
  event: "Eventos",
  payment: "Pagos",
  refund: "Reembolsos",
  lead: "Leads",
  venue: "Venue",
  concepto: "Conceptos",
  metodo_pago: "Métodos de pago",
  cotizacion: "Cotizaciones",
  proveedor: "Proveedores",
};

const ACTION_OPTION_GROUPS = (() => {
  const groups = {};
  Object.values(BITACORA_ACTIONS).forEach((action) => {
    const prefix = action.split(".")[0];
    const groupLabel = ACTION_GROUP_LABELS[prefix] || prefix;
    if (!groups[groupLabel]) groups[groupLabel] = [];
    groups[groupLabel].push({ value: action, label: getActionLabel(action) });
  });
  return Object.entries(groups).map(([label, actions]) => ({
    label,
    actions: actions.sort((a, b) => a.label.localeCompare(b.label, "es")),
  }));
})();

const Bitacora = () => {
  const { selectedVenueId, selectedVenue } = useVenue();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const hasFilters = actionFilter || dateFrom || dateTo || nameFilter;

  const filteredLogs = useMemo(() => {
    const term = nameFilter.trim().toLowerCase();
    if (!term && !actionFilter) return logs;
    return logs.filter((log) => {
      if (actionFilter && log.action !== actionFilter) return false;
      if (term) {
        const userName = (log.userName || "").toLowerCase();
        const entityName = (log.entityName || "").toLowerCase();
        if (!userName.includes(term) && !entityName.includes(term)) return false;
      }
      return true;
    });
  }, [logs, nameFilter, actionFilter]);

  const buildQuery = useCallback(
    (afterDoc) => {
      if (!selectedVenueId) return null;
      const bitacoraCol = collection(db, "venues", selectedVenueId, "bitacora");
      const constraints = [orderBy("timestamp", "desc")];

      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        constraints.push(where("timestamp", ">=", Timestamp.fromDate(fromDate)));
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        constraints.push(where("timestamp", "<=", Timestamp.fromDate(toDate)));
      }

      if (afterDoc) {
        constraints.push(startAfter(afterDoc));
      }
      constraints.push(limit(PAGE_SIZE));

      return query(bitacoraCol, ...constraints);
    },
    [selectedVenueId, dateFrom, dateTo]
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setLogs([]);
    setLastDoc(null);
    setHasMore(true);

    const q = buildQuery(null);
    if (!q) {
      setLoading(false);
      return;
    }

    try {
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLogs(data);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching bitacora:", err);
    }
    setLoading(false);
  }, [buildQuery]);

  const fetchMore = async () => {
    if (!lastDoc || !hasMore) return;
    setLoadingMore(true);
    const q = buildQuery(lastDoc);
    if (!q) {
      setLoadingMore(false);
      return;
    }
    try {
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLogs((prev) => [...prev, ...data]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error fetching more bitacora:", err);
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <ClipboardDocumentListIcon className="h-6 w-6" style={{ color: ACCENT }} />
        <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 dark:text-white">
          Bitácora
        </h1>
        {selectedVenue && (
          <span className="text-lg text-zinc-400 font-normal hidden sm:inline">
            – {selectedVenue.name}
          </span>
        )}
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Registro de todas las acciones realizadas en tu venue.
      </p>

      {/* Filters card */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-4 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: ACCENT + "1a" }}
          >
            <FunnelIcon className="h-4 w-4" style={{ color: ACCENT }} />
          </div>
          <span className="text-sm font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Filtros
          </span>
          {hasFilters && (
            <button
              onClick={() => {
                setActionFilter("");
                setDateFrom("");
                setDateTo("");
                setNameFilter("");
              }}
              className="ml-auto flex items-center gap-1 text-xs font-medium hover:underline"
              style={{ color: ACCENT }}
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              Limpiar
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Acción
            </label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            >
              <option value="">Todas</option>
              {ACTION_OPTION_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.actions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
              Nombre
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <input
                type="text"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Usuario o nombre del registro…"
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-8 pr-3 py-2 text-sm text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main content card */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-zinc-400">Cargando bitácora...</div>
        ) : logs.length === 0 ? (
          <div className="py-16 text-center">
            <ClipboardDocumentListIcon className="h-12 w-12 mx-auto mb-3 text-zinc-300 dark:text-zinc-600" />
            <p className="text-sm text-zinc-400">No hay registros en la bitácora.</p>
            {hasFilters && (
              <button
                onClick={() => {
                  setActionFilter("");
                  setDateFrom("");
                  setDateTo("");
                  setNameFilter("");
                }}
                className="mt-3 text-sm font-medium hover:underline"
                style={{ color: ACCENT }}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm table-fixed">
                <colgroup>
                  <col className="w-[170px]" />
                  <col className="w-[120px]" />
                  <col className="w-[180px]" />
                  <col className="w-[100px]" />
                  <col className="w-[160px]" />
                  <col />
                </colgroup>
                <thead>
                  <tr className="border-b border-zinc-200 dark:border-zinc-700/60">
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Fecha
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Usuario
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Acción
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Entidad
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Nombre
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Detalles
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {filteredLogs.map((log) => {
                    const details = formatDetails(log.details);
                    return (
                      <tr key={log.id} className="group">
                        <td className="px-3 py-2.5 text-zinc-500 dark:text-zinc-400 text-xs whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-3 py-2.5 text-zinc-700 dark:text-zinc-300">
                          {log.userName || "-"}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-zinc-900 dark:text-white">
                          {getActionLabel(log.action)}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge color={ENTITY_BADGE_COLORS[log.entity] || "zinc"}>
                            {ENTITY_LABELS[log.entity] || log.entity}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-zinc-700 dark:text-zinc-300 overflow-hidden max-w-[160px]">
                          <span className="block truncate" title={log.entityName || ""}>{log.entityName || "-"}</span>
                        </td>
                        <td className="px-3 py-2.5 text-sm">
                          {details || <span className="text-zinc-400">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {nameFilter && filteredLogs.length === 0 && logs.length > 0 && hasMore && (
              <div className="border-t border-zinc-200 dark:border-zinc-700/60 px-4 pt-4 text-center text-xs text-zinc-500 dark:text-zinc-400">
                Sin coincidencias en esta página — carga más registros para seguir buscando.
              </div>
            )}

            {/* Load more */}
            {hasMore && (
              <div className="border-t border-zinc-200 dark:border-zinc-700/60 p-4 text-center">
                <button
                  type="button"
                  onClick={fetchMore}
                  disabled={loadingMore}
                  className="text-sm font-medium hover:underline transition disabled:opacity-50"
                  style={{ color: ACCENT }}
                >
                  {loadingMore ? "Cargando..." : "Cargar más registros"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Count */}
      {!loading && logs.length > 0 && (
        <p className="text-xs text-zinc-400 mt-3 text-right">
          {nameFilter.trim()
            ? `${filteredLogs.length} de ${logs.length} mostrados`
            : `${logs.length} registro${logs.length !== 1 ? "s" : ""} mostrado${logs.length !== 1 ? "s" : ""}`}
        </p>
      )}
    </div>
  );
};

export default Bitacora;
